import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import { checkboxClasses } from '@liquidweb-dev/undercurrent/Checkbox';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import LockIcon from '@liquidweb-dev/undercurrent/icons/lucide/Lock';
import ScaleIcon from '@liquidweb-dev/undercurrent/icons/lucide/Scale';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { Theme } from '@liquidweb-dev/undercurrent/styles';
import { switchClasses } from '@liquidweb-dev/undercurrent/Switch';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import HookedSwitch from 'components/standard/Form/HookedSwitch';
import HookedTextField from 'components/standard/Form/HookedTextField';
import { useDialogMakePaymentContext } from '../dialogMakePaymentContext';
import useExpectedBalance from './useExpectedBalance';

const getFooterBackgroundColor = (
	{
		payFullBalance,
		hasExpectedCredit,
	}: { payFullBalance: boolean; hasExpectedCredit: boolean },
	themeVars: Theme['vars'],
) => {
	if (payFullBalance) {
		return themeVars.palette.uc.success.bg.main;
	}
	if (hasExpectedCredit) {
		return themeVars.palette.uc.highlight.bg.main;
	}
	return themeVars.palette.uc.bg.weak;
};

const RemainingBalance = ({ sx, ...rest }: BoxProps) => {
	const { formValues } = useDialogMakePaymentContext();
	const { amount } = formValues;
	const {
		expectedCredit,
		expectedRemainingBalance,
		hasExpectedCredit,
		isLoading,
	} = useExpectedBalance(amount);

	if (isLoading) return null;

	return (
		<Box
			sx={combineSx(
				{
					display: 'flex',
					alignItems: 'center',
					typography: 'body-sm',
				},
				sx,
			)}
			{...rest}
		>
			<ScaleIcon
				sx={({ size, vars }) => ({
					width: size.xS,
					height: size.xS,
					color: vars.palette.uc.icon.weak,
				})}
			/>
			<Collapse in={!hasExpectedCredit} orientation="horizontal">
				<Typography
					sx={{
						color: ({ vars }) => vars.palette.uc.text.weak,
						whiteSpace: 'nowrap',
						marginLeft: 100,
					}}
				>
					Remaining balance:
				</Typography>
			</Collapse>
			<Typography sx={{ fontWeight: 500, marginLeft: 100 }}>
				{formatCurrency(expectedRemainingBalance)}
			</Typography>

			<Collapse in={hasExpectedCredit} orientation="horizontal">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 100,
						marginLeft: 200,
						whiteSpace: 'nowrap',
					}}
				>
					<Typography
						variant="body-sm"
						sx={{ color: ({ vars }) => vars.palette.uc.highlight.text.medium }}
					>
						Acct. credit
					</Typography>
					<Typography
						variant="body-sm"
						sx={{
							fontWeight: 500,
							color: ({ vars }) => vars.palette.uc.highlight.text.strong,
						}}
					>
						{formatCurrency(expectedCredit, { plusSign: true })}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	);
};

const AmountField = () => {
	const { formValues, setFormValues } = useDialogMakePaymentContext();
	const { amount, payFullBalance } = formValues;
	const { accountBalanceDisplay, hasExpectedCredit, isLoading } =
		useExpectedBalance(amount);

	const showPayFullBalanceSwitch = Number(accountBalanceDisplay) > 0;

	return (
		<Stack>
			<HookedTextField
				name="amount"
				type="number"
				disabled={isLoading}
				autoComplete="off"
				slotProps={{
					htmlInput: {
						sx: { [pseudoClasses.important]: { paddingLeft: 0 } },
					},
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<Typography
									variant="body-md"
									sx={{ color: ({ vars }) => vars.palette.uc.text.main }}
								>
									$
								</Typography>
							</InputAdornment>
						),
						endAdornment: payFullBalance && (
							<InputAdornment position="end">
								<LockIcon />
							</InputAdornment>
						),
						readOnly: payFullBalance,
					},
				}}
				sx={{
					'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
						{
							WebkitAppearance: 'none',
							margin: 0,
						},
					'& input[type=number]': {
						MozAppearance: 'textfield',
					},
				}}
			/>
			<Box
				sx={({ borderRadius, vars }) => ({
					backgroundColor: () =>
						getFooterBackgroundColor(
							{ payFullBalance, hasExpectedCredit },
							vars,
						),
					borderRadius: borderRadius.rounded,
					marginTop: -150,
					paddingTop: 300,
					paddingX: 200,
					paddingBottom: 150,
					transition: 'background-color 0.3s ease-in-out',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 100,
				})}
			>
				{isLoading && (
					<Skeleton variant="rounded" sx={{ width: '75%', height: '34px' }} />
				)}
				{showPayFullBalanceSwitch && (
					<HookedSwitch
						name="payFullBalance"
						label="Pay full balance"
						disabled={isLoading}
						onChange={(event) => {
							if (event.target.checked && accountBalanceDisplay) {
								setFormValues((prev) => ({
									...prev,
									amount: accountBalanceDisplay,
								}));
							}
						}}
						sx={({ vars }) => ({
							[`& .${switchClasses.switchBase}.${checkboxClasses.checked} + .${switchClasses.track}`]:
								{
									backgroundColor: vars.palette.uc.success.bg.strong,
								},
						})}
					/>
				)}

				<RemainingBalance
					sx={{
						opacity: !payFullBalance ? 1 : 0,
						transition: 'opacity 0.3s ease-in-out',
					}}
				/>
			</Box>
		</Stack>
	);
};

export default AmountField;
